import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiMapPin, FiBriefcase, FiClock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle } from '../../components/common';
import { careerService } from '../../services/careerService';
import { formatDate } from '../../utils/dateUtils';
import { truncate } from '../../utils/stringUtils';
import { useDebounce } from '../../hooks';
import './CareersPage.css';

const typeLabels = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
  'contract': 'Contract',
  'internship': 'Internship'
};

const typeVariants = {
  'full-time': 'primary',
  'part-time': 'info',
  'contract': 'warning',
  'internship': 'secondary'
};

const CareersPage = () => {
  const navigate = useNavigate();
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 10;

  const fetchCareers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await careerService.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch
      });
      setCareers(response.data.careers);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch career opportunities');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    fetchCareers();
  }, [fetchCareers]);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleToggleStatus = async (id) => {
    try {
      await careerService.toggleStatus(id);
      setCareers(prev =>
        prev.map(item =>
          item.id === id
            ? { ...item, status: item.status === 'open' ? 'closed' : 'open' }
            : item
        )
      );
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await careerService.delete(deleteModal.id);
      toast.success('Career opportunity deleted successfully');
      fetchCareers();
    } catch (error) {
      toast.error('Failed to delete career opportunity');
    } finally {
      setDeleteLoading(false);
      setDeleteModal({ open: false, id: null });
    }
  };

  const columns = [
    {
      header: 'Position',
      accessor: 'title',
      render: (row) => (
        <div className="career-title-cell">
          <span className="career-title">{truncate(row.title, 50)}</span>
          <div className="career-meta">
            <span className="career-meta-item">
              <FiBriefcase size={12} />
              {row.department}
            </span>
            <span className="career-meta-item">
              <FiMapPin size={12} />
              {row.location}
            </span>
            <span className="career-meta-item">
              <FiClock size={12} />
              {row.experience}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Type',
      accessor: 'type',
      width: '120px',
      render: (row) => (
        <Badge variant={typeVariants[row.type] || 'secondary'}>
          {typeLabels[row.type] || row.type}
        </Badge>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      width: '110px',
      render: (row) => (
        <Toggle
          name={`status-${row.id}`}
          checked={row.status === 'open'}
          onChange={() => handleToggleStatus(row.id)}
          size="small"
        />
      )
    },
    {
      header: 'State',
      accessor: 'statusLabel',
      width: '90px',
      render: (row) => (
        <Badge variant={row.status === 'open' ? 'success' : 'danger'}>
          {row.status === 'open' ? 'Open' : 'Closed'}
        </Badge>
      )
    },
    {
      header: 'Posted On',
      accessor: 'createdAt',
      width: '120px',
      render: (row) => (
        <span className="career-date">{formatDate(row.createdAt)}</span>
      )
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button
            className="action-btn edit"
            title="Edit"
            onClick={() => navigate(`/careers/edit/${row.id}`)}
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn delete"
            title="Delete"
            onClick={() => setDeleteModal({ open: true, id: row.id })}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="careers-page">
      <Card
        title="Career Opportunities"
        subtitle={`${totalItems} position${totalItems !== 1 ? 's' : ''}`}
        action={
          <Link to="/careers/add">
            <Button icon={<FiPlus />}>Add Position</Button>
          </Link>
        }
      >
        <DataTable
          columns={columns}
          data={careers}
          loading={loading}
          searchable
          searchPlaceholder="Search by title, department, location…"
          searchValue={search}
          onSearch={handleSearch}
          pagination
          pageSize={pageSize}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          exportable
          exportFilename="careers-export"
          emptyMessage="No career opportunities found"
        />
      </Card>

      <ConfirmDialog
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Career Opportunity"
        message="Are you sure you want to delete this career opportunity? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default CareersPage;
